"use client";

/**
 * `RadioField` — single-choice picker rendered as a Radix RadioGroup
 * with one `RadioGroupItem` per option from `field.options`.
 *
 * Why `Controller`: same as Checkbox/Switch — Radix RadioGroup is a
 * controlled component (`value` + `onValueChange`) rather than a set of
 * native `<input type="radio">` elements, so `register` won't bridge it.
 *
 * Layout: the field's `label` becomes a group caption (rendered as a
 * non-htmlFor `<Label>` since the group itself doesn't have a single
 * focusable id). Each option gets a `<RadioGroupItem id={field.name + "-" + value}>`
 * and a `<Label htmlFor>` next to it so clicks on the option label
 * select the option.
 *
 * Accessibility note: Radix's RadioGroup root already exposes a
 * `role="radiogroup"`, and the group caption is associated via
 * `aria-labelledby` to a `<span id>` rather than a `<Label htmlFor>` —
 * `htmlFor` on a label that points to a non-focusable container is a
 * lint-warn pattern in some validators. We use a plain styled `<span>`
 * for the group caption and let each option's own `<Label>` handle the
 * `htmlFor` binding.
 */

import * as React from "react";
import { Controller } from "react-hook-form";
import { Label, RadioGroup, RadioGroupItem } from "@aio/platform-ui";
import { useFormContext } from "../form-context";
import type { RadioFieldDef } from "../types";
import { readErrorMessage } from "./read-error";
import { resolveFieldPath } from "./path";

export interface RadioFieldProps {
  field: RadioFieldDef;
  /** Path prefix from a parent {@link RepeaterField}. See `text-field.tsx`. */
  pathPrefix?: string;
}

export function RadioField({
  field,
  pathPrefix,
}: RadioFieldProps): React.ReactElement | null {
  const { form } = useFormContext();

  if (field.condition && !field.condition(form.getValues())) {
    return null;
  }

  const path = resolveFieldPath(field.name, pathPrefix);
  const errorMessage = readErrorMessage(form.formState.errors, path);
  const groupCaptionId = `${path}-caption`;

  return (
    <div className="space-y-1.5">
      <span
        id={groupCaptionId}
        className="text-sm font-medium leading-none text-fg"
      >
        {field.label}
      </span>
      <Controller
        name={path}
        control={form.control}
        render={({ field: rhf }) => (
          <RadioGroup
            value={typeof rhf.value === "string" ? rhf.value : ""}
            onValueChange={(next) => {
              rhf.onChange(next);
            }}
            onBlur={rhf.onBlur}
            ref={rhf.ref}
            aria-labelledby={groupCaptionId}
            aria-invalid={errorMessage !== undefined ? true : undefined}
            {...(field.disabled ? { disabled: true } : {})}
          >
            {field.options.map((opt) => {
              const itemId = `${path}-${opt.value}`;
              return (
                <div key={opt.value} className="flex items-center gap-2">
                  <RadioGroupItem
                    id={itemId}
                    value={opt.value}
                    {...(field.disabled ? { disabled: true } : {})}
                  />
                  <Label htmlFor={itemId}>{opt.label}</Label>
                </div>
              );
            })}
          </RadioGroup>
        )}
      />
      {field.description ? (
        <p className="text-xs text-fg-muted">{field.description}</p>
      ) : null}
      {errorMessage ? (
        <p className="text-xs text-danger">{errorMessage}</p>
      ) : null}
    </div>
  );
}
